<template>
  <div class="instruction-panel-entry">
    <ValidationObserver ref="observer">
      <div class="tabs-header">
        <h2 class="h2">{{ $t("instruction-panel.labellisation-qualification.title") }}</h2>
        <TabsDropdownSelector
          :tabs="tabs"
          :active-tab="activeTab"
          :disabled="isSaving"
          @change-tab="changeTab"
        />
        <b-loading v-model="isSavingComputed" :is-full-page="false"></b-loading>
      </div>

      <b-tabs v-model="activeTab" vertical :animated="false" class="block InstructionModale-tabs">
        <b-tab-item :label="$t('instruction.thematiques')">
          <InstructionThematiques
            :projet-annuel="internalProjetAnnuel"
            :read-only="readOnly"
            @projet-annuel-updated="updateProjetAnnuel"
          />
        </b-tab-item>
        <b-tab-item :label="$t('instruction.objets')">
          <InstructionObjetsRealisation
            :projet-annuel="internalProjetAnnuel"
            :read-only="readOnly"
            @projet-annuel-updated="updateProjetAnnuel"
          />
        </b-tab-item>
        <b-tab-item :label="$t('instruction.milieu-generique')">
          <InstructionMilieuxGeneriques
            :projet-annuel="internalProjetAnnuel"
            :read-only="readOnly"
            @projet-annuel-updated="updateProjetAnnuel"
          />
        </b-tab-item>
        <b-tab-item :label="$t('instruction.milieu-interet-particulier')">
          <InstructionMilieuxParticuliers
            :projet-annuel="internalProjetAnnuel"
            :read-only="readOnly"
            @projet-annuel-updated="updateProjetAnnuel"
          />
        </b-tab-item>
      </b-tabs>
      <!-- Save -->
      <div class="bottom-buttons-evaluation">
        <!-- Save / Cancel actions -->
        <div class="buttons">
          <b-button type="is-primary" :disabled="readOnly" @click="save()">
            {{ $t("instruction-panel.instruction.save") }}
          </b-button>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { ProjectService } from "@/services/rest/project/ProjectService"
import TabsDropdownSelector from "@/views/commons/TabsDropdownSelector.vue"
import InstructionMilieuxGeneriques from "@/views/instruction/InstructionMilieuxGeneriques.vue"
import InstructionMilieuxParticuliers from "@/views/instruction/InstructionMilieuxParticuliers.vue"
import InstructionObjetsRealisation from "@/views/instruction/InstructionObjetsRealisation.vue"
import InstructionThematiques from "@/views/instruction/InstructionThematiques.vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { BButton, BLoading, BTabs, BTabItem } from "buefy"

export default defineComponent({
  name: "InstructionPanelLabellisationQualification",
  components: {
    ValidationObserver,
    TabsDropdownSelector,
    InstructionThematiques,
    InstructionObjetsRealisation,
    InstructionMilieuxGeneriques,
    InstructionMilieuxParticuliers,
    BButton,
    BTabs,
    BTabItem,
    BLoading,
  },
  props: {
    project: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      projectService: ProjectService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      internalProjetAnnuel: new ProjetAnnuelDTO(),
      activeTab: 0,
      tabs: [
        this.$t("instruction.thematiques"),
        this.$t("instruction.objets"),
        this.$t("instruction.milieu-generique"),
        this.$t("instruction.milieu-interet-particulier"),
      ],
      isSavingCopy: this.isSaving,
    }
  },
  computed: {
    isSavingComputed: {
      get(): boolean {
        return this.isSavingCopy
      },
      set(value: boolean) {
        this.isSavingCopy = value
      },
    },
  },
  watch: {
    project(): void {
      this.refreshInternalProject()
    },
    isSaving: {
      handler(newIsSaving) {
        this.isSavingCopy = newIsSaving
      },
      immediate: true,
    },
  },
  created(): void {
    this.refreshInternalProject()
  },
  methods: {
    changeTab(tabIndex: number): void {
      this.activeTab = tabIndex
    },

    refreshInternalProject(): void {
      this.internalProjetAnnuel = { ...this.project }
    },

    async save(): Promise<void> {
      try {
        this.internalProjetAnnuel = await this.projectService.updateQualifications(
          this.internalProjetAnnuel,
        )
        this.infoReportingService.success(this.$t("projet.save-success").toString())
      } catch (e: any) {
        this.infoReportingService.error(this.$t("projet.save-error").toString(), e as Error)
      }
    },

    updateProjetAnnuel(updatedProjetAnnuel: ProjetAnnuelDTO): void {
      // Mettre à jour le projet annuel interne
      this.internalProjetAnnuel = updatedProjetAnnuel
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.InstructionModale-tabs {
  &.b-tabs.is-vertical {
    .tabs {
      display: none;
    }
  }

  li {
    line-height: 1.5vh;
  }
}

.tabs-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid $separation-color;
}

.bottom-buttons-evaluation {
  position: absolute;
  right: 80px;
  bottom: 20px;
}
</style>
